import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useRef } from 'react';
import { noop } from 'lodash-es';
import { Page, TableList, PageEmptyState, Button, PageContentHeader, FilterPickerGroup } from 'seagull';
import { Plus } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import TableFilters from '~/components/TableFilters/TableFilters';
import { SearchFilterType } from '~/components/TableFilters/types';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useLocaleKeys } from '~/i18n';
import { ClientRow } from './ClientRow/ClientRow';
import { ClientsBulkActions } from './ClientsBulkActions/ClientsBulkActions';
import { filterClients, sortClients } from './ClientsPage.utils';
import { useRowSelection } from './hooks/useRowSelection';
export const clientsPageTextsPath = 'pages.clientsDashboard';
export const GetClientsPageTID = (parentTestId)=>getTestIds(parentTestId);
const sortIds = [
    'clientName',
    'status',
    'type',
    'owner',
    'lastInvoice',
    'nextInvoice'
];
const columnDefs = [
    {
        width: [
            140,
            300
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_clients_by_name'
        }
    },
    {
        width: [
            100,
            140
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_clients_by_status'
        }
    },
    {
        width: [
            100,
            140
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_clients_by_type'
        }
    },
    {
        width: [
            140,
            250
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_clients_by_owner'
        }
    },
    {
        width: [
            120,
            140
        ],
        sortable: true,
        bi: {
            name: 'sort_clients_by_last_invoice'
        }
    },
    {
        width: [
            120,
            140
        ],
        sortable: true,
        bi: {
            name: 'sort_clients_by_next_invoice'
        }
    }
];
const ALL_CLIENT_TYPES = [
    'business',
    'individual'
];
export function ClientsPage(param) {
    let { clients = [] } = param;
    const { ClientsPageTestIds: TID } = useMemo(()=>GetClientsPageTID(), []);
    const isMobileDevice = useIsMobileForHomePage();
    const { t: clientsPageTexts } = useLocaleKeys(clientsPageTextsPath);
    const { t: columnHeaderTexts } = useLocaleKeys('pages.clientsDashboard.columnHeaders');
    const { t: statusFilterTexts } = useLocaleKeys('pages.clientsDashboard.statusFilter');
    const { t: typeFilterTexts } = useLocaleKeys('pages.clientsDashboard.typeFilter');
    const { t: emptyStateTexts } = useLocaleKeys('pages.clientsDashboard.emptyState');
    const defaultSort = useMemo(()=>({
            colIndex: 0,
            sort: 'desc'
        }), []);
    const { sort: tableSort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const scrollElRef = useRef(null);
    const { urlState: { search, status: statusFilterValue, type: typeFilterValue }, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        },
        status: {
            defaultValue: [
                'active'
            ],
            multi: true
        },
        type: {
            defaultValue: ALL_CLIENT_TYPES,
            multi: true
        }
    });
    const statusFilterOptions = useMemo(()=>[
            {
                value: 'active',
                label: statusFilterTexts.active()
            },
            {
                value: 'archived',
                label: statusFilterTexts.archived()
            }
        ], []);
    const typeFilterOptions = useMemo(()=>[
            {
                value: 'business',
                label: typeFilterTexts.business()
            },
            {
                value: 'individual',
                label: typeFilterTexts.individual()
            }
        ], []);
    const filterLabels = useMemo(()=>({
            status: statusFilterTexts.label(),
            type: typeFilterTexts.label()
        }), []);
    const filterConfig = useMemo(()=>({
            status: {
                type: SearchFilterType.MultiSelectFilter,
                removable: false,
                options: statusFilterOptions,
                mobile: {
                    isEmptyValue: (value)=>{
                        const statuses = value;
                        return !Array.isArray(statuses) || statuses.length === 0 || statuses.length === statusFilterOptions.length;
                    }
                }
            },
            type: {
                type: SearchFilterType.MultiSelectFilter,
                removable: false,
                options: typeFilterOptions,
                mobile: {
                    isEmptyValue: (value)=>{
                        const types = value;
                        return !Array.isArray(types) || types.length === 0 || types.length === typeFilterOptions.length;
                    }
                }
            }
        }), [
        statusFilterOptions,
        typeFilterOptions
    ]);
    const selectedFilters = useMemo(()=>({
            status: statusFilterValue,
            type: typeFilterValue
        }), [
        statusFilterValue,
        typeFilterValue
    ]);
    const headerItems = useMemo(()=>[
            {
                id: 'clientName',
                value: columnHeaderTexts.clientName()
            },
            {
                id: 'status',
                value: columnHeaderTexts.status()
            },
            {
                id: 'type',
                value: columnHeaderTexts.type()
            },
            {
                id: 'owner',
                value: columnHeaderTexts.owner()
            },
            {
                id: 'lastInvoice',
                value: columnHeaderTexts.lastInvoice()
            },
            {
                id: 'nextInvoice',
                value: columnHeaderTexts.nextInvoice()
            }
        ], []);
    const sortOptions = useMemo(()=>[
            {
                id: 'clientName',
                label: columnHeaderTexts.clientName(),
                defaultDirection: 'desc',
                colIndex: 0
            },
            {
                id: 'status',
                label: columnHeaderTexts.status(),
                defaultDirection: 'desc',
                colIndex: 1
            },
            {
                id: 'type',
                label: columnHeaderTexts.type(),
                defaultDirection: 'desc',
                colIndex: 2
            },
            {
                id: 'owner',
                label: columnHeaderTexts.owner(),
                defaultDirection: 'desc',
                colIndex: 3
            },
            {
                id: 'lastInvoice',
                label: columnHeaderTexts.lastInvoice(),
                defaultDirection: 'desc',
                colIndex: 4
            },
            {
                id: 'nextInvoice',
                label: columnHeaderTexts.nextInvoice(),
                defaultDirection: 'desc',
                colIndex: 5
            }
        ], []);
    const filteredAndSorted = useMemo(()=>{
        var _sortIds_colIndex;
        const filtered = filterClients({
            clients,
            searchTerm: search,
            statusFilter: statusFilterValue,
            typeFilter: typeFilterValue
        });
        const { sort, colIndex } = tableSort || {
            colIndex: 0,
            sort: 'asc'
        };
        const sortKey = (_sortIds_colIndex = sortIds[colIndex]) !== null && _sortIds_colIndex !== void 0 ? _sortIds_colIndex : sortIds[0];
        const sortDirection = sort === 'asc' ? 'desc' : 'asc';
        return sortClients({
            clients: filtered,
            sortKey,
            sortDirection
        });
    }, [
        clients,
        search,
        statusFilterValue,
        typeFilterValue,
        tableSort
    ]);
    const { selected, numSelected, numVisibleSelected, setSelected, onToggleAllSelection } = useRowSelection({
        items: filteredAndSorted
    });
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: filteredAndSorted,
        scrollElRef
    });
    const onSortChange = (sort)=>{
        changeSort(sort);
    };
    const handleTableFiltersRefresh = (filters, sort)=>{
        const selectedStatus = filters.status;
        const newStatus = (selectedStatus === null || selectedStatus === void 0 ? void 0 : selectedStatus.length) ? selectedStatus : statusFilterOptions.map((opt)=>opt.value);
        const selectedType = filters.type;
        const newType = (selectedType === null || selectedType === void 0 ? void 0 : selectedType.length) ? selectedType : ALL_CLIENT_TYPES;
        changeUrlState({
            status: newStatus,
            type: newType
        });
        if (sort !== undefined) {
            changeSort(sort);
        }
    };
    const onClearFilters = ()=>{
        changeUrlState({
            search: '',
            status: statusFilterOptions.map((opt)=>opt.value),
            type: ALL_CLIENT_TYPES
        });
    };
    return /*#__PURE__*/ _jsx(Page, {
        testId: TID.Root(),
        ref: scrollElRef,
        header: clientsPageTexts.title(),
        actions: /*#__PURE__*/ _jsx(Button, {
            bi: {
                name: 'create_client'
            },
            testId: TID.CreateButton(),
            prefixIcon: /*#__PURE__*/ _jsx(Plus, {
                size: 18
            }),
            onClick: noop,
            children: clientsPageTexts.createClient()
        }),
        onScroll: onScroll,
        children: /*#__PURE__*/ _jsxs(TableList.Provider, {
            hasActionColumn: true,
            columnDefs: columnDefs,
            sort: tableSort,
            onSortChange: onSortChange,
            enableRowSelection: true,
            selectedRows: selected,
            onSelectionChange: setSelected,
            numRows: filteredAndSorted.length,
            numSelectedRows: numVisibleSelected,
            onToggleAllSelection: onToggleAllSelection,
            children: [
                /*#__PURE__*/ _jsxs(PageContentHeader, {
                    children: [
                        /*#__PURE__*/ _jsxs(FilterPickerGroup, {
                            style: {
                                marginBottom: 24
                            },
                            children: [
                                /*#__PURE__*/ _jsx(TableFilters, {
                                    testIdPrefix: TID.Filters(),
                                    filterLabels: filterLabels,
                                    filterConfig: filterConfig,
                                    selectedFilters: selectedFilters,
                                    setSelectedFilters: noop,
                                    onRefresh: handleTableFiltersRefresh,
                                    search: {
                                        bi: {
                                            name: 'clients_search'
                                        },
                                        value: search,
                                        testId: TID.Search(),
                                        onChange: (event)=>{
                                            changeUrlState({
                                                search: event.target.value
                                            });
                                        },
                                        onClear: ()=>changeUrlState({
                                                search: ''
                                            })
                                    },
                                    mobile: {
                                        sortOptions
                                    }
                                }),
                                !isMobileDevice && /*#__PURE__*/ _jsx(ClientsBulkActions, {
                                    numSelected: numSelected,
                                    testId: TID.BulkActions(),
                                    onBulkArchive: noop,
                                    onBulkDelete: noop
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(TableList.Header, {
                            items: headerItems,
                            testId: TID.TableHeader()
                        })
                    ]
                }),
                filteredAndSorted.length ? paginatedData.map((client)=>/*#__PURE__*/ _jsx(ClientRow, {
                        client: client,
                        onArchiveClient: noop,
                        onUnarchiveClient: noop,
                        onDeleteClient: noop,
                        onEditClient: noop,
                        onViewClient: noop
                    }, client.id)) : /*#__PURE__*/ _jsx(PageEmptyState, {
                    illustration: /*#__PURE__*/ _jsx(_Fragment, {}),
                    title: emptyStateTexts.title(),
                    message: emptyStateTexts.description(),
                    actions: /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'clear_clients_filters'
                        },
                        testId: TID.ClearFiltersButton(),
                        onClick: onClearFilters,
                        children: emptyStateTexts.clearFilters()
                    })
                })
            ]
        })
    });
}
